// LoadingScreen.jsx (refactored: simplified smooth animation, better completion checks, removed unnecessary refs, fixed async state issues)
import { useState, useEffect, useCallback, useRef } from "react";
import "./LoadingScreen.css";
import { useResourcePreloader } from "../../hooks/useResourcePreloader";

const LoadingScreen = ({
  onLoadingComplete,
  onLoadingCompleteAnimation,
  minDisplayTime = 1000,
  resources = [],
}) => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const [show, setShow] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  const { resourcesLoaded, progress: realProgress } =
    useResourcePreloader(resources);

  const targetProgressRef = useRef(0);
  const animationFrameRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const completedRef = useRef(false);

  const completeLoading = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    setIsExiting(true);
    setTimeout(() => {
      setShow(false);
      onLoadingComplete?.();
    }, 600);
  }, [onLoadingComplete]);

  const checkShouldComplete = useCallback(() => {
    const elapsed = Date.now() - startTimeRef.current;
    const shouldComplete = resourcesLoaded && displayProgress >= 99.9;
    if (shouldComplete) {
      onLoadingCompleteAnimation?.();
      if (elapsed < minDisplayTime) {
        setTimeout(() => completeLoading(), minDisplayTime - elapsed); //等待
      } else {
        completeLoading();
      }
    }
  }, [resourcesLoaded, displayProgress, minDisplayTime, completeLoading]);

  // 平滑更新进度
  const updateSmoothProgress = useCallback(() => {
    setDisplayProgress((prevProgress) => {
      const target = targetProgressRef.current;
      if (Math.abs(prevProgress - target) < 0.1) {
        checkShouldComplete();
        return prevProgress;
      }
      const diff = target - prevProgress;
      let step = diff * 0.05; // 平滑因子，调整速度
      if (Math.abs(step) < 0.1) step = diff; // 小步直接到位

      const newProgress = prevProgress + step;
      animationFrameRef.current = requestAnimationFrame(updateSmoothProgress);
      return newProgress;
    });
  }, [checkShouldComplete]);

  // 监听 realProgress 变化
  useEffect(() => {
    targetProgressRef.current = Math.max(
      targetProgressRef.current,
      realProgress,
    ); // 只允许进度前进，不后退
    if (animationFrameRef.current)
      cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = requestAnimationFrame(updateSmoothProgress);
  }, [realProgress, updateSmoothProgress]);

  // 监听 resourcesLoaded
  useEffect(() => {
    if (resourcesLoaded) {
      targetProgressRef.current = 100;
      if (animationFrameRef.current)
        cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = requestAnimationFrame(updateSmoothProgress);
    }
  }, [resourcesLoaded, updateSmoothProgress]);

  // 清理
  useEffect(() => {
    return () => {
      if (animationFrameRef.current)
        cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  if (!show) return null;

  return (
    <div
      className="loading-screen"
      style={{
        opacity: isExiting ? 0 : 1,
        pointerEvents: isExiting ? "none" : "auto",
      }}
    >
      <div className="loading-progress">
        <div
          className="loading-bar"
          style={{
            "--progress": `${displayProgress}%`,
          }}
        />
        <div className="loading-text-container">
          <p className="loading-text-message">LOADING...</p>
          <div className="loading-text-progress">
            {Math.round(displayProgress)}%
          </div>
        </div>
      </div>
      <div className="version-info">
        <div className="version-info-text">ver.2601</div>
      </div>
    </div>
  );
};

export default LoadingScreen;
